import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { 
  Users, 
  Search, 
  Phone, 
  User, 
  Ticket,
  Loader2,
  FileSpreadsheet,
  DollarSign,
  TrendingDown,
  Upload,
  Edit,
  Trash2,
  X,
  Save
} from 'lucide-react';
import { toast } from 'sonner';

const ClientesList = () => {
  const navigate = useNavigate();
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ nombre: '', telefono: '', participaciones: 0 });

  useEffect(() => {
    fetchClientes();
  }, []);

  const fetchClientes = async () => {
    try {
      const response = await axios.get(`${API}/clientes/pagados`);
      setClientes(response.data);
    } catch (error) {
      console.error('Error fetching clientes:', error);
      toast.error('Error al cargar los clientes');
    } finally {
      setLoading(false);
    }
  };

  const filteredClientes = clientes.filter(cliente => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      cliente.nombre?.toLowerCase().includes(term) ||
      cliente.telefono?.includes(term)
    );
  });

  const exportToExcel = () => {
    if (clientes.length === 0) {
      toast.error('No hay clientes para exportar');
      return;
    }

    const headers = ['Posición', 'Nombre', 'Teléfono', 'Números Pagados', 'Fecha de Registro'];
    const rows = clientes.map((c, index) => [
      index + 1,
      c.nombre || '',
      c.telefono || '',
      c.numeros_pagados || 0,
      c.created_at ? new Date(c.created_at).toLocaleDateString('es-AR') : ''
    ]);

    const BOM = '\uFEFF';
    const csvContent = BOM + [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(';'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `clientes_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Archivo descargado correctamente');
  };

  const startEditing = (cliente) => {
    setEditingId(cliente.id);
    setEditData({
      nombre: cliente.nombre,
      telefono: cliente.telefono,
      participaciones: cliente.numeros_pagados || cliente.participaciones || 0
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditData({ nombre: '', telefono: '', participaciones: 0 });
  };

  const saveEdit = async (clienteId) => {
    try {
      await axios.put(`${API}/clientes/${clienteId}`, {
        nombre: editData.nombre,
        telefono: editData.telefono,
        participaciones: parseInt(editData.participaciones) || 0
      });
      toast.success('Cliente actualizado');
      setEditingId(null);
      fetchClientes();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al actualizar');
    }
  };

  const deleteCliente = async (clienteId, nombre) => {
    if (!window.confirm(`¿Eliminar a ${nombre}?`)) return;
    
    try {
      await axios.delete(`${API}/clientes/${clienteId}`);
      toast.success('Cliente eliminado');
      fetchClientes();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar');
    }
  };

  const getTotalNumerosPagados = () => {
    return clientes.reduce((sum, c) => sum + (c.numeros_pagados || 0), 0);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Loader2 className="animate-spin mx-auto mb-4" size={40} />
        <p>Cargando clientes...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-3xl font-bold">Base de Clientes</h2>
          <p className="text-gray-600">Clientes que participaron en tus rifas (ordenados de mayor a menor)</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate('/admin/clientes/importar')}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Upload className="mr-2" size={18} />
            Importar
          </Button>
          <Button
            onClick={exportToExcel}
            className="bg-green-600 hover:bg-green-700"
            disabled={clientes.length === 0}
          >
            <FileSpreadsheet className="mr-2" size={18} />
            Descargar
          </Button>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Clientes</p>
                <p className="text-3xl font-bold text-purple-600">{clientes.length}</p>
              </div>
              <Users className="text-purple-500" size={40} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Participaciones</p>
                <p className="text-3xl font-bold text-green-600">{getTotalNumerosPagados()}</p>
              </div>
              <Ticket className="text-green-500" size={40} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de clientes */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingDown className="text-green-600" size={20} />
                Ranking de Clientes
              </CardTitle>
              <CardDescription>Hacé clic en el lápiz para editar</CardDescription>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-48"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {clientes.length === 0 ? (
            <div className="text-center py-12">
              <Users size={64} className="mx-auto mb-4 text-gray-300" />
              <h3 className="text-xl font-semibold text-gray-500 mb-2">No hay clientes aún</h3>
              <Button onClick={() => navigate('/admin/clientes/importar')} className="mt-4">
                <Upload className="mr-2" size={18} />
                Importar Clientes
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-center py-3 px-2 font-semibold w-12">#</th>
                    <th className="text-left py-3 px-4 font-semibold">Cliente</th>
                    <th className="text-left py-3 px-4 font-semibold">Teléfono</th>
                    <th className="text-center py-3 px-4 font-semibold">Participaciones</th>
                    <th className="text-center py-3 px-4 font-semibold w-24">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClientes.map((cliente, index) => (
                    <tr key={cliente.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-2 text-center">
                        {index < 3 ? (
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                            index === 0 ? 'bg-yellow-400 text-yellow-900' :
                            index === 1 ? 'bg-gray-300 text-gray-700' :
                            'bg-amber-600 text-amber-100'
                          }`}>
                            {index + 1}
                          </span>
                        ) : (
                          <span className="text-gray-500 font-medium">{index + 1}</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editingId === cliente.id ? (
                          <Input
                            value={editData.nombre}
                            onChange={(e) => setEditData({...editData, nombre: e.target.value})}
                            className="w-full"
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                              <User size={16} className="text-purple-600" />
                            </div>
                            <span className="font-medium">{cliente.nombre}</span>
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {editingId === cliente.id ? (
                          <Input
                            value={editData.telefono}
                            onChange={(e) => setEditData({...editData, telefono: e.target.value})}
                            className="w-full"
                          />
                        ) : (
                          <div className="flex items-center gap-1 text-gray-600">
                            <Phone size={14} />
                            {cliente.telefono}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {editingId === cliente.id ? (
                          <Input
                            type="number"
                            value={editData.participaciones}
                            onChange={(e) => setEditData({...editData, participaciones: e.target.value})}
                            className="w-20 mx-auto text-center"
                          />
                        ) : (
                          <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 bg-green-100 text-green-700 rounded-full font-bold">
                            {cliente.numeros_pagados || 0}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {editingId === cliente.id ? (
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" onClick={() => saveEdit(cliente.id)} className="bg-green-600 hover:bg-green-700 h-8 w-8 p-0">
                              <Save size={14} />
                            </Button>
                            <Button size="sm" variant="outline" onClick={cancelEditing} className="h-8 w-8 p-0">
                              <X size={14} />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" variant="outline" onClick={() => startEditing(cliente)} className="h-8 w-8 p-0">
                              <Edit size={14} />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => deleteCliente(cliente.id, cliente.nombre)} className="h-8 w-8 p-0 text-red-600 hover:text-red-700">
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientesList;
